import styles from './index.less';
import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import { Button } from 'antd';
import FrozenRatio from '../../components/FrozenRatio/FrozenRatio';
import { FormattedMessage, useIntl } from 'react-intl';
import UnoptimizedImage from '../UnoptimizedImage';

const YOUTUBE_IFRAME = 'https://www.youtube.com/iframe_api';
const scriptId = 'youtube-script-cover';

async function loadYoutubePlayerPlugin(id) {
  const scriptTag = document.getElementById(scriptId);

  if (scriptTag) {
    Promise.resolve();
    return;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.onload = function () {
      typeof resolve === 'function' && resolve();
    };
    script.onerror = reject;
    script.src = YOUTUBE_IFRAME;
    script.id = id;
    document.body.append(script);
  });
}

function initialYoutube(playerId, videoId, muted, callback) {
  let player = new window.YT.Player(playerId, {
    videoId,
    width: '100%',
    height: '100%',
    playerVars: {
      autoplay: 1,
      controls: 1,
      showinfo: 0, // <- This part here
      loop: 1,
      disablekb: 1,
      muted: muted ? 1 : 0,
      playsinline: 1,
      'webkit-playsinline': 1,
      rel: 0,
      enablejsapi: 1,
      cc_load_policy: 0,
      // playlist: videoId,
      modestbranding: 1,
      iv_load_policy: 3,
      playlist: videoId,
    },
    events: {
      onReady: (e) => {
        muted && e.target.mute();
        e.target.playVideo();
        callback({ playing: false });
      },
      onStateChange: (e) => {
        // console.log('onStateChange', e);
        callback({ playing: true });
        if (window.YT && e.data === window.YT.PlayerState.ENDED) {
          // player.playVideo();
          callback({ playing: true });
        }
      },
    },
  });

  return player;
}

export default function AutoPlayer({
  poster,
  posterAlt,
  posterRatio = 1,
  youtubeVideoId = '',
  autoPlayOnCover = false,
  centerButtonType = 'pureButton',
  autoClickToPlayWhenOpen,
  playerOuterStyle,
  canPause = false,
}) {
  const ref = useRef();
  const videoPlayerRef = useRef(null);
  const [videoPlayingOnCover, setVideoPlayingOnCover] = useState(true);
  const [playButtonOnClicked, setPlayButtonOnClicked] = useState(false);
  const [firstPlaying, setFirstPlaying] = useState(false);

  async function loadVideo(playerId, videoId, muted, callback) {
    let videoPlayer = null;
    try {
      loadYoutubePlayerPlugin(scriptId);
      if (window.YT && window.YT.Player) {
        videoPlayer = initialYoutube(playerId, videoId, muted, ({ playing }) => {
          callback({
            player: videoPlayer,
            playing,
          });
        });
      } else {
        window.onYouTubeIframeAPIReady = function () {
          videoPlayer = initialYoutube(playerId, videoId, muted, ({ playing }) => {
            callback({
              player: videoPlayer,
              playing,
            });
          });
          window.onYouTubeIframeAPIReady = null;
        };
      }
    } catch (e) {
      console.log('init player error: ', e);
    }
  }

  useEffect(() => {
    if (autoPlayOnCover && youtubeVideoId) {
      loadVideo(
        'chowbus_one_player_cover',
        youtubeVideoId,
        autoClickToPlayWhenOpen ? false : true,
        ({ player, playing }) => {
          videoPlayerRef.current = player;
          setFirstPlaying((current) => {
            if (current) {
              return current;
            }
            return playing;
          });
        }
      );
    }
  }, [autoPlayOnCover, youtubeVideoId, autoClickToPlayWhenOpen]);

  useEffect(() => {
    if (autoPlayOnCover && youtubeVideoId) {
      const element = ref.current;
      if (!element) return;
      element.addEventListener('contextmenu', (e) => e.preventDefault());
    }
  }, [autoPlayOnCover, youtubeVideoId]);

  const onPlayOrPauseRequest = useCallback(() => {
    if (videoPlayerRef.current) {
      if (!videoPlayingOnCover) {
        videoPlayerRef.current.playVideo();
        setVideoPlayingOnCover(true);
      } else {
        videoPlayerRef.current.pauseVideo();
        setVideoPlayingOnCover(false);
      }
    }
  }, [videoPlayingOnCover]);

  const onPlay = useCallback(() => {
    const onStateChangeEventListener = (event) => {
      if (event.data === window.YT.PlayerState.ENDED) {
        setPlayButtonOnClicked(false);
        videoPlayerRef.current.setLoop(true);
        videoPlayerRef.current.mute();
        videoPlayerRef.current.playVideo();
        videoPlayerRef.current.removeEventListener('onStateChange', onStateChangeEventListener);
      }
    };

    if (videoPlayerRef.current && !playButtonOnClicked) {
      // if (!videoPlayingOnCover) {

      setVideoPlayingOnCover(true);
      setPlayButtonOnClicked(true);
      videoPlayerRef.current.setLoop(true);
      videoPlayerRef.current.unMute();
      videoPlayerRef.current.playVideo();
      videoPlayerRef.current.addEventListener('onStateChange', onStateChangeEventListener);
    }
    return () => {
      videoPlayerRef.current && videoPlayerRef.current.removeEventListener('onStateChange', onStateChangeEventListener);
    };
  }, [videoPlayingOnCover, playButtonOnClicked]);

  useEffect(() => {
    autoClickToPlayWhenOpen && setPlayButtonOnClicked(true);
  }, [autoClickToPlayWhenOpen]);

  return (
    <div
      className={`${styles.autoplayer} ${firstPlaying ? styles.playing : ''}`}
      style={playerOuterStyle ? { ...playerOuterStyle } : {}}
    >
      {(!autoPlayOnCover || !youtubeVideoId) && (
        <div className="poster-img-container " id="chowbus_one_player_cover" ref={ref}>
          <FrozenRatio ratio={posterRatio}>
            <UnoptimizedImage src={poster} alt={posterAlt} layout="fill" />
          </FrozenRatio>
        </div>
      )}
      {autoPlayOnCover && youtubeVideoId && (
        <FrozenRatio ratio={9 / 16} mainClassName={styles.playerContainer}>
          <div className="poster-img-container " id="chowbus_one_player_cover" ref={ref}>
            <FrozenRatio ratio={posterRatio}>
              <img width="100%" src={poster} alt={posterAlt} />
            </FrozenRatio>
          </div>
        </FrozenRatio>
      )}

      {autoPlayOnCover && youtubeVideoId && !playButtonOnClicked && (
        <div
          className={`playerTopCover ${!playButtonOnClicked ? 'playing' : ''}`}
          onClick={canPause && playButtonOnClicked ? onPlayOrPauseRequest : () => {}}
        >
          {centerButtonType === 'pureButton' ? (
            <span
              className={`playerButton pureButton ${playButtonOnClicked ? 'playerButtonClicked' : ''}`}
              onClick={onPlay}
            ></span>
          ) : (
            <span className={`playerButton ${playButtonOnClicked ? 'playerButtonClicked' : ''}`} onClick={onPlay}>
              <FormattedMessage id="home.chowbuspos.seechowbusworks" />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
